import { useState, type FormEvent } from "react";
import { ApiError, OfflineError } from "../lib/api";
import { syncEngine } from "../lib/sync";
import { keyFromScan, unlockWithKeyMaterial, unlockWithRecoveryCode } from "../lib/encryption";
import { Button, TextField } from "./ui";
import { QrScanner } from "./QrScanner";
import { LockIcon, QrIcon } from "./icons";
import { useT } from "../i18n/index.js";

/**
 * Пространство зашифровано, а ключа на этом устройстве нет.
 *
 * Этот экран занимает место содержимого целиком, и это не строгость ради
 * строгости: без ключа списки, переписка и календарь — это ряды заглушек, а
 * экран, полный заглушек, читается как сломанное приложение, а не как запертая
 * дверь. Шапка остаётся на месте, поэтому уйти в другое пространство или в
 * настройки — и выйти оттуда — можно всегда.
 *
 * Дорог две, и главная — первая. Отсканировать квадрат с телефона, где
 * пространство уже открыто, занимает секунду и не требует ничего помнить; код
 * восстановления — это то, к чему возвращаются, когда открытых телефонов не
 * осталось. Поэтому подсказка про камеру стоит выше поля, а не под ним мелким
 * шрифтом.
 */
export function SpaceLocked({
  spaceId,
  onUnlocked,
}: {
  spaceId: string;
  onUnlocked: () => void;
}) {
  const t = useT();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);

  /*
   * Возвращает `true` только когда квадрат подошёл — иначе сканер продолжает
   * смотреть. Чужой квадрат, попавший в кадр, не должен закрывать камеру и
   * заставлять начинать заново.
   */
  function accept(text: string): boolean {
    const material = keyFromScan(spaceId, text);

    if (!material) {
      // Ссылка наша, но от другого пространства — это стоит сказать отдельно:
      // человек держит перед камерой правильный на вид квадрат.
      setScanError(
        text.includes("/join?") ? t("encryption.scanWrongSpace") : t("encryption.scanNotOurs"),
      );
      return false;
    }

    setScanError(null);
    setScanning(false);

    void (async () => {
      try {
        await unlockWithKeyMaterial(spaceId, material);
        onUnlocked();
      } catch {
        setError(t("encryption.scanNotOurs"));
      }
    })();

    return true;
  }

  async function unlock(event: FormEvent) {
    event.preventDefault();
    if (busy || code.trim().length === 0) return;

    setBusy(true);
    setError(null);
    try {
      // Регистр, пробелы и дефисы приводит к одному виду сама функция: человек
      // переписывает код с экрана или из бумажки, и отказать ему из-за лишнего
      // пробела — это звонок «у меня пропали данные».
      await unlockWithRecoveryCode(spaceId, code);
      // Дельта, пришедшая до ключа, лежит нерасшифрованной; проход за ней
      // заново — это то, что зажигает экраны содержимым.
      await syncEngine.pull(spaceId);
      onUnlocked();
    } catch (cause) {
      // Поле не очищается ни при какой ошибке: код длинный, и заставлять
      // набирать его снова из-за одной опечатки — наказание не по вине.
      setError(
        cause instanceof OfflineError
          ? t("encryption.unlockOffline")
          : cause instanceof ApiError
            ? cause.message
            : t("encryption.wrongCode"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4 px-4 pt-8 pb-8">
      <span
        aria-hidden="true"
        className="grid size-12 place-items-center rounded-full bg-surface-alt text-muted"
      >
        <LockIcon className="size-6" strokeWidth={1.8} />
      </span>

      <div className="flex flex-col gap-2">
        <h1 className="font-display text-2xl">{t("encryption.lockedTitle")}</h1>
        <p className="text-ink-soft">{t("encryption.lockedBody")}</p>
      </div>

      <div className="flex flex-col gap-3 rounded-card bg-surface px-4 py-4 shadow-soft">
        <div className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className="grid size-10 shrink-0 place-items-center rounded-full bg-accent-soft text-accent-ink"
          >
            <QrIcon className="size-5" strokeWidth={1.8} />
          </span>
          <p className="min-w-0 flex-1 text-[0.875rem] text-ink-soft">
            {t("encryption.lockedScan")}
          </p>
        </div>

        {scanning ? (
          <>
            <QrScanner onFound={accept} onCancel={() => setScanning(false)} />
            {scanError ? (
              <p role="status" className="px-1 text-[0.875rem] text-neg">
                {scanError}
              </p>
            ) : null}
          </>
        ) : (
          <Button
            full
            onClick={() => {
              setScanError(null);
              setScanning(true);
            }}
          >
            <QrIcon className="size-[18px]" strokeWidth={1.9} />
            {t("encryption.scan")}
          </Button>
        )}
      </div>

      <form onSubmit={unlock} className="flex flex-col gap-3" noValidate>
        <p className="px-1 text-[0.875rem] text-muted">{t("encryption.lockedOr")}</p>

        <TextField
          label={t("encryption.codeField")}
          hint={t("encryption.codeHint")}
          value={code}
          onChange={(event) => {
            setCode(event.target.value);
            setError(null);
          }}
          error={error ?? undefined}
          autoComplete="off"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          className="font-mono tracking-wider"
        />

        <Button type="submit" disabled={busy || code.trim().length === 0}>
          {busy ? t("encryption.unlocking") : t("encryption.unlock")}
        </Button>
      </form>
    </div>
  );
}
